import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, FileText, BarChart3, PieChart, TrendingUp, Users, DollarSign, Calendar, Award } from 'lucide-react';
import axios from 'axios';

const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [membershipReport, setMembershipReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [eventsReport, setEventsReport] = useState(null);
  const [cpdReport, setCpdReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [mem, fin, evt, cpd] = await Promise.all([
        axios.get(`${backendUrl}/api/executive/reports/membership`, { headers }),
        axios.get(`${backendUrl}/api/executive/reports/financial`, { headers }),
        axios.get(`${backendUrl}/api/executive/reports/events`, { headers }),
        axios.get(`${backendUrl}/api/executive/reports/cpd`, { headers }),
      ]);
      setMembershipReport(mem.data);
      setFinancialReport(fin.data);
      setEventsReport(evt.data);
      setCpdReport(cpd.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              REPORTS
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
            <p className="mt-4 text-lg text-white/90">
              Executive reports and analytics for informed decision making
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Membership Report */}
            <Card>
              <CardHeader className="bg-blue-500 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Users size={24} />
                  </div>
                  <CardTitle className="text-xl">Membership Report</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{membershipReport?.total_users || 0}</p>
                    <p className="text-sm text-gray-600">Total Members</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{membershipReport?.doctors || 0}</p>
                    <p className="text-sm text-gray-600">Doctors</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{membershipReport?.students || 0}</p>
                    <p className="text-sm text-gray-600">Students</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{membershipReport?.new_members_30d || 0}</p>
                    <p className="text-sm text-gray-600">New (30 days)</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{membershipReport?.approved || 0}</p>
                    <p className="text-sm text-gray-600">Approved</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{membershipReport?.pending_approval || 0}</p>
                    <p className="text-sm text-gray-600">Pending Approval</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{membershipReport?.paid_users || 0}</p>
                    <p className="text-sm text-gray-600">Paid Members</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{membershipReport?.pending_payments || 0}</p>
                    <p className="text-sm text-gray-600">Pending Payments</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Report */}
            <Card>
              <CardHeader className="bg-green-500 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <DollarSign size={24} />
                  </div>
                  <CardTitle className="text-xl">Financial Report</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">R {(financialReport?.total_revenue || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">R {(financialReport?.membership_revenue || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Membership Revenue</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">R {(financialReport?.event_revenue || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Event Revenue</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">R {(financialReport?.education_revenue || 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-600">Education Revenue</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{financialReport?.total_transactions || 0}</p>
                    <p className="text-sm text-gray-600">Total Transactions</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{financialReport?.pending_payments || 0}</p>
                    <p className="text-sm text-gray-600">Pending Payments</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Events Report */}
            <Card>
              <CardHeader className="bg-purple-500 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Calendar size={24} />
                  </div>
                  <CardTitle className="text-xl">Events Report</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{eventsReport?.total_events || 0}</p>
                    <p className="text-sm text-gray-600">Total Events</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{eventsReport?.total_registrations || 0}</p>
                    <p className="text-sm text-gray-600">Total Registrations</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{eventsReport?.paid_registrations || 0}</p>
                    <p className="text-sm text-gray-600">Paid Registrations</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{eventsReport?.upcoming_events || 0}</p>
                    <p className="text-sm text-gray-600">Upcoming Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CPD & Education Report */}
            <Card>
              <CardHeader className="bg-orange-500 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Award size={24} />
                  </div>
                  <CardTitle className="text-xl">CPD & Education Report</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{cpdReport?.total_resources || 0}</p>
                    <p className="text-sm text-gray-600">Total Resources</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{cpdReport?.published_resources || 0}</p>
                    <p className="text-sm text-gray-600">Published</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{cpdReport?.paid_resources || 0}</p>
                    <p className="text-sm text-gray-600">Paid Resources</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#2c3e7d]">{cpdReport?.total_access_grants || 0}</p>
                    <p className="text-sm text-gray-600">Access Grants</p>
                  </div>
                </div>
                {cpdReport?.top_cpd_members && cpdReport.top_cpd_members.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Top CPD Members</h4>
                    <div className="space-y-2">
                      {cpdReport.top_cpd_members.map((m, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium">{m.full_name}</span>
                          <span className="text-sm text-[#2c3e7d] font-bold">{m.points} points</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 max-w-6xl mx-auto">
          <Card className="bg-gradient-to-r from-[#2c3e7d] to-[#1e2a5a] text-white">
            <CardContent className="p-8 text-center">
              <BarChart3 size={48} className="mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3">Reports Updated in Real-Time</h3>
              <p className="mb-6 text-white/90">
                These reports are generated from live data. All metrics are calculated on-demand from the database.
              </p>
              <Button variant="secondary" onClick={fetchReports}>
                Refresh Reports
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reports;